import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { errorMessage } from "@/lib/errors";
import { trackEvent } from "@/lib/analytics";

const MAX_POLLS = 10;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null });
  const polls = useRef(0);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: "Missing session id" });
      return;
    }
    let cancelled = false;
    const tick = async () => {
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          refresh();
          // Fire the standard GA4 `purchase` event so revenue lands in the funnel.
          const dollars = (data.amount_total || data.amount || 0) / 100 || null;
          trackEvent("purchase", {
            transaction_id: sessionId,
            value: dollars,
            currency: (data.currency || "usd").toUpperCase(),
            items: [{
              item_id: data.lookup_key || "checkout",
              item_name: data.lookup_key || "checkout",
              price: dollars,
              quantity: 1,
            }],
          });
          // For match-reveal purchases, whisk the user straight to the celebratory reveal on /matches
          if (data.lookup_key === "match_reveal" && data.match_user_id) {
            try {
              const res = await api.get("/matches");
              const revealed = (res.data.matches || []).find(
                (m) => m.unlocked && m.user?.id === data.match_user_id
              );
              if (revealed) {
                nav(`/matches?reveal=${encodeURIComponent(revealed.match_id)}`, { replace: true });
                return;
              }
            } catch { /* fall through to normal success screen */ }
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ phase: "failed", data });
          return;
        }
        if (polls.current >= MAX_POLLS) {
          setState({ phase: "timeout", data });
          return;
        }
        setTimeout(tick, POLL_MS);
      } catch (err) {
        setState({ phase: "error", data: errorMessage(err, "Could not check status") });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      {state.phase === "polling" && (
        <>
          <Loader2 className="w-10 h-10 animate-spin text-[#722f37] mx-auto" />
          <h1 className="font-serif-display text-4xl mt-6">Confirming your payment…</h1>
          <p className="text-[#5c5c5c] mt-3">This usually takes a couple of seconds.</p>
        </>
      )}
      {state.phase === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="w-14 h-14 text-[#722f37] mx-auto" />
          <h1 className="font-serif-display text-5xl mt-6">You're all set.</h1>
          <p className="text-[#5c5c5c] mt-3 text-lg">
            {state.data.lookup_key === "match_reveal"
              ? "Your match has been unlocked."
              : "Your membership is active. Enjoy unlimited match reveals."}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/matches" className="btn-primary" data-testid="payment-to-matches">See my matches</Link>
            <Link to="/discover" className="btn-outline">Keep discovering</Link>
          </div>
        </div>
      )}
      {(state.phase === "failed" || state.phase === "error") && (
        <div data-testid="payment-failed">
          <XCircle className="w-14 h-14 text-[#a02b2b] mx-auto" />
          <h1 className="font-serif-display text-4xl mt-6">Something went wrong.</h1>
          <p className="text-[#5c5c5c] mt-3">{typeof state.data === "string" ? state.data : "Please try again."}</p>
          <div className="mt-8"><Link to="/pricing" className="btn-primary">Back to pricing</Link></div>
        </div>
      )}
      {state.phase === "timeout" && (
        <div data-testid="payment-timeout">
          <Loader2 className="w-10 h-10 text-[#722f37] mx-auto" />
          <h1 className="font-serif-display text-4xl mt-6">Still processing…</h1>
          <p className="text-[#5c5c5c] mt-3">We'll update your account as soon as Stripe confirms.</p>
          <div className="mt-8"><Link to="/matches" className="btn-primary">Go to matches</Link></div>
        </div>
      )}
    </div>
  );
}
